
import { Link } from 'react-router-dom'
import { ArrowLeft, Leaf, Star } from 'lucide-react'
import { communityImages } from '@/assets/images'

const OurStoryPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-primary-50 relative">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{
          backgroundImage: `url('${communityImages.communityGathering2}')`
        }}
      ></div>
      <div className="relative z-10">
      {/* Hero Section */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-6"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            
            <div className="flex justify-center mb-6">
              <img 
                src="/harvesthelp-logo.png"
                alt="HarvestHelp Logo" 
                className="h-16 w-auto"
              />
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Story</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The journey of HarvestHelp and how we're making a difference in communities
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Creator Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="text-center">
            <div className="text-6xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">HarvestHelp by Garrett Zhou</h2>
            <div className="text-gray-600 mb-6 space-y-4">
              <p>
                HarvestHelp is a platform that connects food suppliers, volunteers, and food banks to reduce food waste and ensure no one goes hungry. However, this journey started 
                from a personal experience. I was 15, about to go into high school, and was volunteering at the Food Bank of Central and Eastern North Carolina. I was shocked to see how much food was being wasted, and how many people were going hungry. I knew I had to do something to help, and that's when I decided to create HarvestHelp.
              </p>
              
              <p>
                I started doing some research, and I found that hunger was a much larger problem than I had ever imagined. I knew I had to do something to help, and that's when I decided to create HarvestHelp.
              </p>
              
              <p>
                I started by creating a website that would allow food suppliers to post surplus food that would otherwise go to waste. I also created a platform that would allow volunteers to coordinate pickups and deliveries to food banks.
                Within that same platform, food banks could receive the resources they need to serve their communities and ensure that no one goes hungry. 
              </p>
              
              <p>
                I also research food insecurity more broadly. Explore interactive data and insights on my research site,{' '}
                <a href="https://globalhungerdashboard.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  Global Hunger Dashboard
                </a>
                .
              </p>
              
              <p>
                I am also a passionate writer. I really like informing the public, and I have a personal blog that I write on. Here, I post semi-monthly articles about hunger and food insecurity. Some recent topics are 'Ugly Produce' and Household Food Waste. If you're interested, check it out <a href="https://garrbearsblog.wordpress.com/" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">here</a>.
              </p>
            </div>
            <div className="flex items-center justify-center text-sm text-gray-500">
              <Star className="h-4 w-4 mr-1" />
              <span>Content will be added here</span>
            </div>
          </div>
        </div>

        {/* Mission Section */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <img 
              src="/harvesthelp-logo.png"
              alt="HarvestHelp Logo" 
              className="h-6 w-auto mr-2"
            />
            Our Mission
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            HarvestHelp bridges the gap between food banks, suppliers, and volunteers to reduce 
            food waste and ensure no one goes hungry. We believe that by connecting these three 
            essential groups, we can create a sustainable food ecosystem that benefits everyone.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Our platform makes it easy for food suppliers to donate surplus food, for volunteers 
            to coordinate pickups and deliveries, and for food banks to receive the resources 
            they need to serve their communities.
          </p>
        </div>

        {/* How It Works teaser */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">How It Works</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Want the full walkthrough of what volunteers, suppliers, and food banks each do —
            including claims, confirmations, and credits? We moved the detailed guide to its own page.
          </p>
          <Link to="/how-it-works" className="btn-primary inline-flex items-center">
            See How It Works
          </Link>
        </div>

        {/* Impact Section */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Leaf className="h-6 w-6 text-green-600 mr-2" />
            Our Impact
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-sm font-semibold text-green-700 mb-2">Less waste</div>
              <div className="text-sm text-green-700">More food redirected to the community</div>
            </div>
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-sm font-semibold text-blue-700 mb-2">More reach</div>
              <div className="text-sm text-blue-700">Suppliers and food banks coordinating faster</div>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-sm font-semibold text-purple-700 mb-2">Volunteer-powered</div>
              <div className="text-sm text-purple-700">Deliveries supported by community members</div>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-sm font-semibold text-orange-700 mb-2">Partnerships</div>
              <div className="text-sm text-orange-700">Building long-term local support networks</div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <Link
            to="/"
            className="btn-primary inline-flex items-center text-lg px-8 py-3"
          >
            <img 
              src="/harvesthelp-logo.png"
              alt="HarvestHelp Logo" 
              className="mr-2 h-5 w-auto"
            />
            Back to Home
            <ArrowLeft className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </div>
      </div>
    </div>
  )
}

export default OurStoryPage
